import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import * as api from './api';

function useTenantId(): string {
  const tenantId = useAuthStore((s) => s.tenantUser?.tenantId);
  if (!tenantId) throw new Error('No active tenant');
  return tenantId;
}

const KEYS = {
  list: (tid: string) => ['connectors', 'list', tid] as const,
  grants: (tid: string, cid: string) => ['connectors', 'grants', tid, cid] as const,
  memberGrants: (tid: string, mid: string) => ['connectors', 'member-grants', tid, mid] as const,
  myGrants: (tid: string) => ['connectors', 'my-grants', tid] as const,
};

export function useConnectors() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: KEYS.list(tenantId),
    queryFn: () => api.listConnectors(tenantId),
  });
}

export function useConnectorGrants(connectorId: string) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: KEYS.grants(tenantId, connectorId),
    queryFn: () => api.listGrants(tenantId, connectorId),
    enabled: Boolean(connectorId),
  });
}

export function useMemberConnectorGrants(memberId: string) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: KEYS.memberGrants(tenantId, memberId),
    queryFn: () => api.getMemberConnectorGrants(tenantId, memberId),
  });
}

export function useSaveApiKey() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ connectorId, apiKey }: { connectorId: string; apiKey: string }) =>
      api.saveApiKey(tenantId, connectorId, apiKey),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEYS.list(tenantId) }),
  });
}

export function useBeginOAuth() {
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({
      connectorId,
      clientId,
      clientSecret,
    }: {
      connectorId: string;
      clientId: string;
      clientSecret: string;
    }) => api.beginOAuth(tenantId, connectorId, clientId, clientSecret),
  });
}

export function useDisconnectConnector() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectorId: string) => api.disconnectConnector(tenantId, connectorId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEYS.list(tenantId) }),
  });
}

export function useCreateGrant(connectorId: string) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, allowlist }: { memberId: string; allowlist: string[] | null }) =>
      api.createGrant(tenantId, connectorId, memberId, allowlist),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.grants(tenantId, connectorId) });
    },
  });
}

export function usePatchGrant(connectorId: string) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, allowlist }: { memberId: string; allowlist: string[] | null }) =>
      api.patchGrant(tenantId, connectorId, memberId, allowlist),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.grants(tenantId, connectorId) });
    },
  });
}

export function useRevokeGrant(connectorId: string) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => api.revokeGrant(tenantId, connectorId, memberId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.grants(tenantId, connectorId) });
    },
  });
}

export function useMyGrants() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: KEYS.myGrants(tenantId),
    queryFn: () => api.getMyGrants(tenantId),
    staleTime: 30 * 1000,
  });
}

export function useInvokeConnector(connectorId: string) {
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({
      operationName,
      input,
    }: {
      operationName: string;
      input: Record<string, unknown>;
    }) => api.invokeConnector(tenantId, connectorId, operationName, input),
  });
}

export function useToggleMemberGrant(memberId: string) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      connectorId,
      currentlyGranted,
    }: {
      connectorId: string;
      currentlyGranted: boolean;
    }) => {
      if (currentlyGranted) {
        return api.revokeGrant(tenantId, connectorId, memberId);
      } else {
        return api.createGrant(tenantId, connectorId, memberId, null);
      }
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: KEYS.memberGrants(tenantId, memberId) });
      void qc.invalidateQueries({ queryKey: KEYS.grants(tenantId, vars.connectorId) });
    },
  });
}
